import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from '../modules/auth';
import { api, readTenantSlug } from '@viilutalous/common';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registrationRequired, setRegistrationRequired] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState('');
  const [setupRequired, setSetupRequired] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState({
    checked: false,
    isUpToDate: true,
  });

  const checkMigrationStatus = async (tenantId) => {
    try {
      const response = await api.get(`/${tenantId}/migration_status`);
      setMigrationStatus({
        checked: true,
        isUpToDate: response.data.is_current,
      });
    } catch (error) {
      // Silently fail - don't block users if check fails
      console.warn('Migration status check failed:', error);
      setMigrationStatus({ checked: true, isUpToDate: true });
    }
  };

  const checkSetupStatus = async (tenantId) => {
    try {
      const response = await api.get(`/${tenantId}/company_info/setup_status`);
      setSetupRequired(!response.data.setup_complete);
    } catch (error) {
      // Silently fail - don't block users if check fails
      console.warn('Setup status check failed:', error);
      setSetupRequired(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchTenants();
    } else {
      setTenants([]);
      setCurrentTenant(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const fetchTenants = async () => {
    try {
      // Get user's accessible tenants from the backend
      const response = await api.get('/user/tenants');
      const userTenants = response.data.tenants || [];

      setRegistrationRequired(false);
      setTenants(userTenants);

      // Priority: slug in URL path > saved tenant from localStorage > first tenant.
      // The URL slug wins so pasted/shared links open the right tenant for the recipient.
      if (userTenants.length > 0) {
        const urlSlug = typeof window !== 'undefined' ? readTenantSlug(window.location.pathname) : null;
        const slugTenant = urlSlug ? userTenants.find((t) => t.slug === urlSlug) : null;
        const savedTenantId = localStorage.getItem('selected_tenant_id');
        const savedTenant = userTenants.find((t) => t.id === savedTenantId);

        const selectedTenant = slugTenant || savedTenant || userTenants[0];
        localStorage.setItem('selected_tenant_id', selectedTenant.id);
        setCurrentTenant(selectedTenant);
        // Check migration status for the selected tenant
        checkMigrationStatus(selectedTenant.id);
        // Check if company info setup is complete
        checkSetupStatus(selectedTenant.id);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);

      // Check if this is a REGISTRATION_REQUIRED error - user doesn't exist in our database
      // Show the registration page so they can create a tenant or request access
      const errorDetail = error.response?.data?.detail;
      if (error.response?.status === 403 && errorDetail?.code === 'REGISTRATION_REQUIRED') {
        console.warn('User not found in database, showing registration:', errorDetail.email);
        setRegistrationRequired(true);
        setRegistrationEmail(errorDetail.email || user?.email || '');
        setTenants([]);
        setCurrentTenant(null);
        setLoading(false);
        return;
      }

      // If endpoint doesn't exist yet, create mock tenant from user data
      if (user && user.uid) {
        const mockTenant = {
          id: '54455354-e29b-41d4-a716-446655440001',
          name: 'Musakauppa Oy (Viilu demo)',
          display_name: 'Musakauppa Oy (Viilu demo)',
        };
        setTenants([mockTenant]);
        setCurrentTenant(mockTenant);
        localStorage.setItem('selected_tenant_id', mockTenant.id);
        checkMigrationStatus(mockTenant.id);
        checkSetupStatus(mockTenant.id);
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshTenants = () => {
    setLoading(true);
    fetchTenants();
  };

  const selectTenant = (tenant) => {
    setCurrentTenant(tenant);
    localStorage.setItem('selected_tenant_id', tenant.id);
    checkMigrationStatus(tenant.id);
    checkSetupStatus(tenant.id);
  };

  const value = {
    tenants,
    currentTenant,
    selectTenant,
    loading,
    migrationStatus,
    registrationRequired,
    registrationEmail,
    setupRequired,
    checkSetupStatus,
    refreshTenants,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};
